// Password policy analysis for the forced-password-reset step on the login
// page. Ported from reference/scopely-frontend/src/lib/passwordPolicy.ts's
// pure analysis functions (length / numeric-only / identity-resemblance).
//
// Deliberately DROPS the reference's live HIBP breach-check (checkPasswordBreach,
// PasswordBreachResult, the api.pwnedpasswords.com call): that is a third-party
// network dependency with no measured fixture in contract-fixtures/scopely/auth/
// and is out of C3's scope (field validation UX driven by the measured login
// contract). Divergence noted in the module README.
export const MIN_PASSWORD_LENGTH = 12;

export interface PasswordIdentity {
  email?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
}

export interface PasswordPolicyAnalysis {
  meetsLength: boolean;
  notNumeric: boolean;
  resemblesIdentity: boolean;
  hasIdentityHints: boolean;
}

function normalizeToken(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function splitIdentityPart(value?: string): string[] {
  if (!value) return [];
  return value
    .split(/[@._\-+/\s]+/)
    .map(normalizeToken)
    .filter((part) => part.length >= 3);
}

export function getPasswordIdentityTokens(identity: PasswordIdentity = {}): string[] {
  const tokens = new Set<string>();
  for (const part of splitIdentityPart(identity.email)) tokens.add(part);
  for (const part of splitIdentityPart(identity.username)) tokens.add(part);
  for (const part of splitIdentityPart(identity.firstName)) tokens.add(part);
  for (const part of splitIdentityPart(identity.lastName)) tokens.add(part);
  return [...tokens];
}

export function analyzePasswordPolicy(
  password: string,
  identity: PasswordIdentity = {},
): PasswordPolicyAnalysis {
  const normalizedPassword = normalizeToken(password);
  const identityTokens = getPasswordIdentityTokens(identity);

  return {
    meetsLength: password.length >= MIN_PASSWORD_LENGTH,
    notNumeric: !/^\d+$/.test(password),
    resemblesIdentity:
      identityTokens.length > 0 &&
      identityTokens.some((token) => token.length >= 3 && normalizedPassword.includes(token)),
    hasIdentityHints: identityTokens.length > 0,
  };
}
